import axios from 'axios';
import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { toErrorMessage } from '../api/client';
import {
  fetchBranding,
  fetchBrandingLogo,
  removeBrandingLogo,
  saveBranding,
  uploadBrandingLogo,
} from '../api/endpoints';
import type { ApiErrorBody, BrandingRadius, BrandingSettings, BrandingTheme } from '../api/types';
import { useApiData } from '../hooks/useApiData';
import { ConfirmDialog } from './ConfirmDialog';
import { ErrorState, LoadingState } from './LoadingState';
import { useToast } from './ToastProvider';

/** Mirrors IdVerify.Core.Helpers.BrandingRules — the server enforces these; here they guide the inputs. */
export const BRANDING_LOGO_LIMITS = {
  maxUploadBytes: 2 * 1024 * 1024,
  accept: 'image/png,image/jpeg,image/webp',
};
const MAX_DISPLAY_NAME = 40;
const MIN_ACCENT_CONTRAST = 3;
const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * verify-app's palettes (packages/verify-app/src/styles.css) and default accent, so the preview
 * looks like what customers get. Keep in step with verify-app; the backgrounds are also the ones
 * BrandingRules checks the accent's contrast against.
 */
const PALETTES = {
  dark: { bg: '#0b0d12', panel: '#151924', text: '#f2f5f9', muted: '#9aa4b2', ok: '#2fbf71' },
  light: { bg: '#ffffff', panel: '#f2f4f7', text: '#111827', muted: '#5b6472', ok: '#1a7f4b' },
} as const;
const DEFAULT_ACCENT = '#4f46e5'; // InertiaID indigo, verify-app's --accent
const RADII: Record<BrandingRadius, { card: number; button: number }> = {
  square: { card: 4, button: 4 },
  rounded: { card: 14, button: 12 },
  pill: { card: 20, button: 999 },
};

const THEME_OPTIONS: { value: BrandingTheme; label: string; hint: string }[] = [
  { value: 'dark', label: 'Dark', hint: 'The default' },
  { value: 'light', label: 'Light', hint: 'White background' },
  { value: 'auto', label: 'Automatic', hint: "Follows the customer's phone" },
];
const RADIUS_OPTIONS: { value: BrandingRadius; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'pill', label: 'Pill' },
];

type FieldErrors = Partial<Record<'displayName' | 'accentColor' | 'theme' | 'radius' | 'logo', string[]>>;

function luminance(hex: string): number {
  const channel = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

function contrast(a: string, b: string): number {
  const [la, lb] = [luminance(a), luminance(b)];
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Same rule as BrandingRules.TextColorOn: white when it meets 4.5:1, else the better of black/white. */
function textOn(accent: string): string {
  const onWhite = contrast(accent, '#ffffff');
  if (onWhite >= 4.5) return '#ffffff';
  return contrast(accent, '#000000') > onWhite ? '#000000' : '#ffffff';
}

/** The first background (for this theme) the accent is too faint on, with the ratio. */
function contrastProblem(accent: string, theme: BrandingTheme): { which: 'dark' | 'light'; ratio: number } | null {
  const backgrounds: ('dark' | 'light')[] = theme === 'auto' ? ['dark', 'light'] : [theme];
  for (const which of backgrounds) {
    const ratio = contrast(accent, PALETTES[which].bg);
    if (ratio < MIN_ACCENT_CONTRAST) return { which, ratio };
  }
  return null;
}

/**
 * Branding: how the client's customers see verify-app and the SDK screens — logo, display name,
 * accent colour, theme and corners — with a live preview. Rendered on Settings → Branding (own
 * client, no clientId; read-only unless ClientAdmin) and Client detail → Branding (SuperAdmin).
 * Rules in Core BrandingRules / BrandingService.
 */
export function BrandingForm({ clientId, readOnly = false }: { clientId?: number; readOnly?: boolean }) {
  const { data, loading, error } = useApiData(() => fetchBranding(clientId), [clientId]);

  if (loading) return <LoadingState label="Loading branding" />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <ErrorState message="Branding could not be loaded." />;

  return <Form clientId={clientId} initial={data} readOnly={readOnly} />;
}

function Form({
  clientId,
  initial,
  readOnly,
}: {
  clientId?: number;
  initial: BrandingSettings;
  readOnly: boolean;
}) {
  const { showSuccess, showError } = useToast();
  const [settings, setSettings] = useState(initial);
  const [displayName, setDisplayName] = useState(initial.displayName ?? '');
  const [useCustomAccent, setUseCustomAccent] = useState(initial.accentColor !== null);
  const [accent, setAccent] = useState(initial.accentColor ?? DEFAULT_ACCENT);
  const [theme, setTheme] = useState<BrandingTheme>(initial.theme);
  const [radius, setRadius] = useState<BrandingRadius>(initial.radius);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [previewMode, setPreviewMode] = useState<'dark' | 'light'>(initial.theme === 'light' ? 'light' : 'dark');
  const logoUrl = useLogoUrl(settings.hasLogo ? settings.logoHash : null, clientId);
  const fileInput = useRef<HTMLInputElement>(null);

  // The preview follows the theme; "auto" lets the viewer flip between the two.
  useEffect(() => {
    if (theme !== 'auto') setPreviewMode(theme);
  }, [theme]);

  const accentValid = HEX.test(accent);
  const effectiveAccent = useCustomAccent && accentValid ? accent.toLowerCase() : DEFAULT_ACCENT;
  const problem = useCustomAccent && accentValid ? contrastProblem(accent, theme) : null;

  function applySaved(saved: BrandingSettings) {
    setSettings(saved);
    setDisplayName(saved.displayName ?? '');
    setUseCustomAccent(saved.accentColor !== null);
    setAccent(saved.accentColor ?? DEFAULT_ACCENT);
    setTheme(saved.theme);
    setRadius(saved.radius);
  }

  function errorsFrom(err: unknown): FieldErrors | undefined {
    return axios.isAxiosError(err)
      ? ((err.response?.data as ApiErrorBody | undefined)?.errors as FieldErrors | undefined)
      : undefined;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setFieldErrors({});
    try {
      applySaved(
        await saveBranding(
          {
            displayName: displayName.trim() || null,
            accentColor: useCustomAccent ? accent.trim() : null,
            theme,
            radius,
          },
          clientId,
        ),
      );
      showSuccess('Branding saved. New verifications use it straight away.');
    } catch (err) {
      const errors = errorsFrom(err);
      if (errors) setFieldErrors(errors);
      showError(toErrorMessage(err, 'An error occurred while saving. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setFieldErrors((e) => ({ ...e, logo: undefined }));
    if (file.size > BRANDING_LOGO_LIMITS.maxUploadBytes) {
      setFieldErrors((e) => ({ ...e, logo: ['The logo can be at most 2 MB.'] }));
      return;
    }
    setUploading(true);
    try {
      // Only the logo changes; unsaved edits to the other fields stay in the form.
      const saved = await uploadBrandingLogo(file, clientId);
      setSettings((s) => ({ ...s, hasLogo: saved.hasLogo, logoHash: saved.logoHash, isCustomised: true }));
      showSuccess('Logo uploaded.');
    } catch (err) {
      const errors = errorsFrom(err);
      if (errors?.logo) setFieldErrors((e) => ({ ...e, logo: errors.logo }));
      showError(toErrorMessage(err, 'The logo could not be uploaded.'));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function handleRemove() {
    setRemoving(true);
    try {
      const saved = await removeBrandingLogo(clientId);
      setSettings((s) => ({ ...s, hasLogo: saved.hasLogo, logoHash: saved.logoHash }));
      showSuccess('Logo removed.');
    } catch (err) {
      showError(toErrorMessage(err));
    } finally {
      setRemoving(false);
      setConfirmRemove(false);
    }
  }

  const nameErrors = fieldErrors.displayName ?? [];
  const accentErrors = fieldErrors.accentColor ?? [];
  const logoErrors = fieldErrors.logo ?? [];

  return (
    <div className="row g-4">
      <div className="col-xl-7">
        <form onSubmit={handleSubmit}>
          <fieldset disabled={readOnly} className="m-0 p-0 border-0" style={{ minWidth: 0 }}>
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="mb-0 fw-semibold">
                  <i className="bi bi-image me-2 text-primary" />
                  Logo and name
                </h6>
              </div>
              <div className="card-body">
                <div className="mb-4">
                  <span className="form-label fw-semibold d-block">Logo</span>
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div
                      className="border rounded d-flex align-items-center justify-content-center bg-body-tertiary"
                      style={{ width: 160, height: 64 }}
                    >
                      {logoUrl ? (
                        <img src={logoUrl} alt="Current logo" style={{ maxWidth: 148, maxHeight: 52 }} />
                      ) : (
                        <span className="small text-body-secondary">No logo</span>
                      )}
                    </div>
                    <input
                      ref={fileInput}
                      id="brandingLogo"
                      type="file"
                      accept={BRANDING_LOGO_LIMITS.accept}
                      className="d-none"
                      onChange={(e) => void handleFile(e.target.files?.[0])}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={uploading}
                      onClick={() => fileInput.current?.click()}
                    >
                      {uploading ? (
                        <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
                      ) : (
                        <i className="bi bi-upload me-1" />
                      )}
                      {settings.hasLogo ? 'Replace' : 'Upload'}
                    </button>
                    {settings.hasLogo && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => setConfirmRemove(true)}
                      >
                        <i className="bi bi-trash me-1" />
                        Remove
                      </button>
                    )}
                  </div>
                  {logoErrors.map((m) => (
                    <div className="invalid-feedback d-block" key={m}>
                      {m}
                    </div>
                  ))}
                  <div className="form-text">
                    PNG, JPEG or WebP, up to 2 MB. A wide logo on a transparent background works best. We
                    scale it to at most 512 pixels and store a clean copy (SVG isn't accepted). The logo
                    saves as soon as you upload it.
                  </div>
                </div>

                <div>
                  <label htmlFor="brandingDisplayName" className="form-label fw-semibold">
                    Display name
                  </label>
                  <input
                    id="brandingDisplayName"
                    type="text"
                    className={`form-control ${nameErrors.length ? 'is-invalid' : ''}`}
                    placeholder="Acme Rentals"
                    maxLength={MAX_DISPLAY_NAME}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                  {nameErrors.map((m) => (
                    <div className="invalid-feedback" key={m}>
                      {m}
                    </div>
                  ))}
                  <div className="form-text">
                    Shown next to your logo, so customers know who is asking. Leave blank to show only the
                    logo.
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h6 className="mb-0 fw-semibold">
                  <i className="bi bi-palette me-2 text-primary" />
                  Colours and style
                </h6>
              </div>
              <div className="card-body">
                <div className="mb-4">
                  <span className="form-label fw-semibold d-block">Theme</span>
                  <div className="btn-group" role="group" aria-label="Theme">
                    {THEME_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        className={`btn btn-sm ${theme === o.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                        aria-pressed={theme === o.value}
                        title={o.hint}
                        onClick={() => setTheme(o.value)}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <div className="form-text">
                    {THEME_OPTIONS.find((o) => o.value === theme)?.hint}.
                  </div>
                </div>

                <div className="mb-4">
                  <div className="form-check form-switch mb-2">
                    <input
                      id="brandingCustomAccent"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={useCustomAccent}
                      onChange={(e) => setUseCustomAccent(e.target.checked)}
                    />
                    <label htmlFor="brandingCustomAccent" className="form-check-label fw-semibold">
                      Use my own accent colour
                    </label>
                  </div>
                  {useCustomAccent && (
                    <div className="d-flex align-items-start gap-2" style={{ maxWidth: 260 }}>
                      <input
                        type="color"
                        className="form-control form-control-color"
                        aria-label="Accent colour picker"
                        value={accentValid ? accent : DEFAULT_ACCENT}
                        onChange={(e) => setAccent(e.target.value)}
                      />
                      <div className="flex-grow-1">
                        <input
                          id="brandingAccent"
                          type="text"
                          aria-label="Accent colour (hex)"
                          className={`form-control font-monospace ${
                            accentErrors.length || !accentValid ? 'is-invalid' : ''
                          }`}
                          placeholder="#1a73e8"
                          maxLength={7}
                          value={accent}
                          onChange={(e) => setAccent(e.target.value)}
                        />
                        {!accentValid && <div className="invalid-feedback">Use a hex colour such as #1a73e8.</div>}
                        {accentErrors.map((m) => (
                          <div className="invalid-feedback" key={m}>
                            {m}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {problem && (
                    <div className="alert alert-warning small mt-2 mb-0 py-2" role="status">
                      <i className="bi bi-exclamation-triangle me-1" />
                      This colour is too close to the {problem.which} background ({problem.ratio.toFixed(1)}:1).
                      Buttons need at least {MIN_ACCENT_CONTRAST}:1 to stay visible, so it can't be saved — pick a{' '}
                      {problem.which === 'light' ? 'darker' : 'lighter'} shade.
                    </div>
                  )}
                  <div className="form-text">
                    Used for buttons and highlights. Button text switches between black and white
                    automatically so it stays readable.
                  </div>
                </div>

                <div>
                  <span className="form-label fw-semibold d-block">Corners</span>
                  <div className="btn-group" role="group" aria-label="Corners">
                    {RADIUS_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        className={`btn btn-sm ${radius === o.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                        aria-pressed={radius === o.value}
                        onClick={() => setRadius(o.value)}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {readOnly ? (
              <p className="text-body-secondary small">Only a company admin can change the branding.</p>
            ) : (
              <button type="submit" className="btn btn-primary" disabled={saving || (useCustomAccent && !accentValid) || !!problem}>
                {saving && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
                Save branding
              </button>
            )}
          </fieldset>
        </form>
      </div>

      <div className="col-xl-5">
        <div className="card position-sticky" style={{ top: '1rem' }}>
          <div className="card-header d-flex align-items-center justify-content-between">
            <h6 className="mb-0 fw-semibold">
              <i className="bi bi-phone me-2 text-primary" />
              Preview
            </h6>
            {theme === 'auto' && (
              <div className="btn-group btn-group-sm" role="group" aria-label="Preview mode">
                {(['dark', 'light'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className={`btn ${previewMode === mode ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    aria-pressed={previewMode === mode}
                    onClick={() => setPreviewMode(mode)}
                  >
                    {mode === 'dark' ? 'Dark phone' : 'Light phone'}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="card-body">
            <div className="d-flex gap-3 justify-content-center flex-wrap">
              <PhonePreview
                screen="intro"
                mode={previewMode}
                accent={effectiveAccent}
                radius={radius}
                logoUrl={logoUrl}
                displayName={displayName.trim()}
              />
              <PhonePreview
                screen="result"
                mode={previewMode}
                accent={effectiveAccent}
                radius={radius}
                logoUrl={logoUrl}
                displayName={displayName.trim()}
              />
            </div>
            <p className="form-text text-center mb-0 mt-3">
              The verification page and the SDK screens use these settings. Developers can still override
              them in the SDK.
            </p>
          </div>
        </div>
      </div>

      <ConfirmDialog
        show={confirmRemove}
        title="Remove logo"
        message="Customers will see your display name only (or nothing, if it's blank). You can upload a logo again at any time."
        confirmLabel="Remove logo"
        busy={removing}
        onConfirm={() => void handleRemove()}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}

/** Object URL of the current logo, refetched when the hash changes; revoked on change and unmount. */
function useLogoUrl(logoHash: string | null, clientId?: number): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!logoHash) {
      setUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchBrandingLogo(clientId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [logoHash, clientId]);

  return url;
}

/**
 * A small mock of verify-app's start or result screen. Purely visual (aria-hidden content is
 * described by the label), drawn with the same palette, sizes and copy as the real page.
 */
function PhonePreview({
  screen,
  mode,
  accent,
  radius,
  logoUrl,
  displayName,
}: {
  screen: 'intro' | 'result';
  mode: 'dark' | 'light';
  accent: string;
  radius: BrandingRadius;
  logoUrl: string | null;
  displayName: string;
}) {
  const p = PALETTES[mode];
  const r = RADII[radius];
  const frame: CSSProperties = {
    width: 200,
    height: 380,
    borderRadius: 26,
    border: `6px solid ${mode === 'dark' ? '#2b2f3a' : '#d7dbe2'}`,
    background: p.bg,
    color: p.text,
    display: 'flex',
    flexDirection: 'column',
    padding: '14px 12px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 11,
    textAlign: 'left',
    overflow: 'hidden',
  };
  const button: CSSProperties = {
    marginTop: 'auto',
    borderRadius: r.button,
    background: accent,
    color: textOn(accent),
    textAlign: 'center',
    fontWeight: 600,
    padding: '9px 0',
    fontSize: 11,
  };

  return (
    <figure className="m-0 text-center">
      <div style={frame} role="img" aria-label={`Preview of the ${screen === 'intro' ? 'start' : 'result'} screen, ${mode} mode`}>
        <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {(logoUrl || displayName) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, minHeight: 22 }}>
              {logoUrl && <img src={logoUrl} alt="" style={{ maxHeight: 22, maxWidth: 90 }} />}
              {displayName && <span style={{ fontWeight: 600, fontSize: 11 }}>{displayName}</span>}
            </div>
          )}

          {screen === 'intro' ? (
            <>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Verify your identity</div>
              <div style={{ color: p.muted, lineHeight: 1.4, marginBottom: 8 }}>
                We need a quick identity check. It takes about a minute.
              </div>
              <div style={{ background: p.panel, borderRadius: r.card, padding: '8px 10px' }}>
                {['Liveness check', 'Front of ID', 'Back of ID'].map((step, i) => (
                  <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: i === 0 ? p.ok : p.muted,
                        flex: 'none',
                      }}
                    />
                    {step}
                  </div>
                ))}
              </div>
              <div style={button}>Start verification</div>
            </>
          ) : (
            <>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, textAlign: 'center' }}>
                <span
                  style={{
                    padding: '3px 9px',
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 9,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    background: 'rgba(47, 191, 113, 0.15)',
                    color: p.ok,
                  }}
                >
                  Approved
                </span>
                <div style={{ fontSize: 14, fontWeight: 700 }}>You're verified!</div>
                <div style={{ color: p.muted, lineHeight: 1.4 }}>Thanks — we'll take you back to where you started.</div>
              </div>
              <div style={button}>Continue now</div>
            </>
          )}
        </div>
      </div>
      <figcaption className="small text-body-secondary mt-1">{screen === 'intro' ? 'Start' : 'Result'}</figcaption>
    </figure>
  );
}
